import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcLeite } from './CalcleiteContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Receita.css';
import Config from '../../config/config.json';
import tags from '../../config/tags.json';

// Funções auxiliares de formatação
const formatCurrencyForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(String(value).replace(/\D/g, '')) / 100;
  if (isNaN(number)) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const Receita = () => {
  const { dados, setDados } = useCalcLeite();
  const navigate = useNavigate();

  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [mostrarAjuda, setMostrarAjuda] = useState(false);

  useEffect(() => {
    if (!dados.receita) {
      setDados(prev => ({
        ...prev,
        receita: { leiteVendido: '', precoLeiteVendido: '', outrasReceitas: '' }
      }));
    }
    if (dados.resultadosReceita) {
      setMostrarResultados(true);
    }
  }, [dados.receita, dados.resultadosReceita, setDados]);

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/\D/g, '');
    setDados(prev => ({
      ...prev,
      receita: { ...prev.receita, [name]: rawValue }
    }));
  };

  const handleCalcular = () => {
    // 1. Puxa dados das telas anteriores do contexto
    const leiteVendido = (Number(dados.receita?.leiteVendido) || 0) / 100;
    const custoOperacional = parseFloat(dados.resultadosCustos?.custoOperacional) || 0;
    const custoTotal = parseFloat(dados.resultadosCustos?.custoTotal) || 0;
    const custoOperacionalLitro = parseFloat(dados.resultadosCustos?.custoOperacionalLitro) || 0;
    const rentabilidadeCapital = parseFloat(dados.resultadosCapital?.rentabilidadeCapital) || 0;
    const depreciacao = parseFloat(dados.resultadosCapital?.depreciacao) || 0;

    if (!custoOperacional || !custoTotal) {
      alert("Dados das telas anteriores (Custos, Capital) não encontrados. Por favor, calcule-os primeiro.");
      return;
    }
    
    // 2. Puxa dados dos inputs desta tela
    const precoLeiteVendido = (Number(dados.receita?.precoLeiteVendido) || 0) / 100;
    const outrasReceitas = (Number(dados.receita?.outrasReceitas) || 0) / 100;

    // 3. Executa os cálculos
    const receitaVendaLeite = leiteVendido * precoLeiteVendido;
    const receitaBrutaTotal = receitaVendaLeite + outrasReceitas;
    const lucroBruto = receitaBrutaTotal - custoOperacional;
    const lucroLiquido = receitaBrutaTotal - custoTotal;
    const lucroBrutoLitro = leiteVendido ? lucroBruto / leiteVendido : 0;
    const lucroLiquidoLitro = leiteVendido ? lucroLiquido / leiteVendido : 0;
    const pontoEquilibrio = (precoLeiteVendido - custoOperacionalLitro) > 0
        ? (depreciacao + rentabilidadeCapital) / (precoLeiteVendido - custoOperacionalLitro)
        : 0;
    const relacaoRentabilidade = rentabilidadeCapital !== 0
        ? (lucroLiquido / rentabilidadeCapital) * 100
        : 0;

    const resultados = {
        receitaBrutaTotal: receitaBrutaTotal.toFixed(2),
        receitaVendaLeite: receitaVendaLeite.toFixed(2),
        lucroBruto: lucroBruto.toFixed(2),
        lucroLiquido: lucroLiquido.toFixed(2),
        lucroBrutoLitro: lucroBrutoLitro.toFixed(2),
        lucroLiquidoLitro: lucroLiquidoLitro.toFixed(2),
        pontoEquilibrio: pontoEquilibrio.toFixed(2),
        relacaoRentabilidade: relacaoRentabilidade.toFixed(2),
    };

    setDados(prev => ({ ...prev, resultadosReceita: resultados }));
    setMostrarResultados(true);
  };

  const formatCurrencyResult = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatNumberResult = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  const handleDownloadPDF = () => {
    if (!dados.receita || !dados.resultadosReceita) {
      alert("Por favor, calcule os resultados antes de gerar o PDF.");
      return;
    }
    const doc = new jsPDF();
    const r = dados.receita;
    const res = dados.resultadosReceita;
    
    // Cabeçalho com fundo azul
    doc.setFillColor(30, 144, 255); // #1e90ff
    doc.rect(0, 0, 210, 40, 'F'); // Cabeçalho com 40mm de altura
    doc.setTextColor(255, 255, 255); // Texto branco
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text("Relatório de Receita e Rentabilidade", 105, 20, { align: 'center' });

    // Data centralizada abaixo do título
    doc.setTextColor(200, 200, 200); // Cinza claro
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const dataFormatada = new Date(dados.producao?.dataSelecionada).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    doc.text(`Data: ${dataFormatada}`, 105, 30, { align: 'center' });

    // Adicionar imagens no cabeçalho
    const custosAgroImg = Config.colors.base64.custosagro;
    const appImg = Config.colors.base64[tags.tags.app];
    if (custosAgroImg) {
      doc.addImage(custosAgroImg, 'PNG', 10, 7.5, 25, 25); // Logo CustosAgro mais à esquerda
    }
    if (appImg) {
      doc.addImage(appImg, 'PNG', 175, 7.5, 25, 25); // Logo do app mais à direita
    }

    // Tabela de Dados Inseridos
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(11);
    const dadosInseridosBody = [
      ['Leite Vendido', `${formatNumberResult(Number(r.leiteVendido) / 100)} L`],
      ['Preço do Leite Vendido', `${formatCurrencyResult(Number(r.precoLeiteVendido) / 100)}/L`],
      ['Outras Receitas', `${formatCurrencyResult(Number(r.outrasReceitas) / 100)}`],
    ];

    autoTable(doc, {
      startY: 50,
      head: [['Descrição', 'Valor Inserido']],
      body: dadosInseridosBody,
      theme: 'striped',
      headStyles: { fillColor: [44, 62, 80] },
    });

    // Tabela de Resultados Calculados
    const resultadosBody = [
      ['Receita Bruta Total', `${formatCurrencyResult(res.receitaBrutaTotal)}`],
      ['Receita com Venda de Leite', `${formatCurrencyResult(res.receitaVendaLeite)}`],
      ['Lucro Bruto (Operacional)', `${formatCurrencyResult(res.lucroBruto)}`],
      ['Lucro Líquido', `${formatCurrencyResult(res.lucroLiquido)}`],
      ['Lucro Bruto por Litro', `${formatCurrencyResult(res.lucroBrutoLitro)}`],
      ['Lucro Líquido por Litro', `${formatCurrencyResult(res.lucroLiquidoLitro)}`],
      ['Ponto de Equilíbrio', `${formatNumberResult(res.pontoEquilibrio)} L`],
      ['Relação Rentabilidade (Leite vs Mercado)', `${formatNumberResult(res.relacaoRentabilidade)}%`],
    ];

    autoTable(doc, {
      head: [['Indicador', 'Resultado Calculado']],
      body: resultadosBody,
      theme: 'grid',
      headStyles: { fillColor: [30, 144, 255] },
    });

    // Adicionar numeração de páginas
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(`Relatório gerado por CustosAgro - Página ${i} de ${pageCount}`, 105, 287, { align: 'center' });
    }
    
    doc.save("relatorio_receita.pdf");
  };

  const handleLimparDados = () => {
    if (window.confirm("Tem certeza que deseja limpar os dados desta tela?")) {
      setDados(prev => {
        const { receita, resultadosReceita, ...resto } = prev;
        localStorage.setItem('calcLeiteData', JSON.stringify(resto));
        return resto;
      });
      setMostrarResultados(false);
    }
  };

  return (
    <div className="receita-container">
      <div className="receita-card">
        <h1>Receita e Rentabilidade</h1>
        <p className="receita-subtitle">Dados para calcular a receita e rentabilidade.</p>

        <div className="receita-form">
          <div className="receita-input-group">
            <label>Leite Vendido (litros)</label>
            <input type="text" name="leiteVendido" placeholder="Ex: 14.500,00" value={formatCurrencyForDisplay(dados.receita?.leiteVendido)} onChange={handleCurrencyChange} />
          </div>
          <div className="receita-input-group">
            <label>Preço do Leite Vendido/Litro (R$)</label>
            <input type="text" name="precoLeiteVendido" placeholder="Ex: 2,50" value={formatCurrencyForDisplay(dados.receita?.precoLeiteVendido)} onChange={handleCurrencyChange} />
          </div>
          <div className="receita-input-group">
            <label>Outras Receitas (R$)</label>
            <input type="text" name="outrasReceitas" placeholder="Ex: 1.200,00" value={formatCurrencyForDisplay(dados.receita?.outrasReceitas)} onChange={handleCurrencyChange} />
          </div>
          <button className="receita-button" onClick={handleCalcular}>Calcular</button>
          <button className="receita-help-button" onClick={() => setMostrarAjuda(true)}>Precisa de ajuda?</button>
        </div>

        {mostrarResultados && (
          <div className="receita-results">
            <h2>Resultados</h2>
            <ul>
              <li>Receita Bruta Total: <strong>{formatCurrencyResult(dados.resultadosReceita?.receitaBrutaTotal)}</strong></li>
              <li>Receita com Venda de Leite: <strong>{formatCurrencyResult(dados.resultadosReceita?.receitaVendaLeite)}</strong></li>
              <li>Lucro Bruto (Operacional): <strong>{formatCurrencyResult(dados.resultadosReceita?.lucroBruto)}</strong></li>
              <li>Lucro Líquido: <strong>{formatCurrencyResult(dados.resultadosReceita?.lucroLiquido)}</strong></li>
              <li>Lucro Bruto por Litro: <strong>{formatCurrencyResult(dados.resultadosReceita?.lucroBrutoLitro)}</strong></li>
              <li>Lucro Líquido por Litro: <strong>{formatCurrencyResult(dados.resultadosReceita?.lucroLiquidoLitro)}</strong></li>
              <li>Ponto de Equilíbrio: <strong>{formatNumberResult(dados.resultadosReceita?.pontoEquilibrio)} L</strong></li>
              <li>Relação Rentabilidade (Leite vs Mercado): <strong>{formatNumberResult(dados.resultadosReceita?.relacaoRentabilidade)} %</strong></li>
            </ul>
            <button className="receita-button pdf" onClick={handleDownloadPDF}>Baixar Relatório (PDF)</button>
          </div>
        )}

        <div className="receita-navigation">
          <button className="receita-button back" onClick={() => navigate('/calcleite/app/custos')}>Voltar para Custos</button>
          <button className="receita-button clean" onClick={handleLimparDados}>Limpar Dados</button>
          <button className="receita-button navigate" onClick={() => navigate("/calcleite/app/relatorio")}>Avançar para Relatório</button>
        </div>
      </div>
      
      {mostrarAjuda && (
        <div className="ajuda-modal-backdrop" onClick={() => setMostrarAjuda(false)}>
          <div className="ajuda-modal-content" onClick={e => e.stopPropagation()}>
            <h2>Ajuda - Receita e Rentabilidade</h2>
            <p><strong>Outras Vendas:</strong> Podem ser descarte de animais, adubo orgânico, venda de bezerros, queijo, etc.</p>
            <p><strong>Ponto de Equilíbrio:</strong> É a quantidade de leite que o produtor precisa produzir para pagar todos os seus custos (sem lucro nem prejuízo). Se o resultado for negativo, significa que o preço do leite está abaixo do custo de produção por litro.</p>
            <button onClick={() => setMostrarAjuda(false)}>Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receita;